import React, {
  useState,
  ChangeEvent,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Button } from "../atoms/button";
import { CameraIcon } from "../icons/camera-icon";

// Define the props for the component
interface MobileCameraCaptureProps {
  onPictureTaken: (timestamp: number, file: File) => void;
  showPreview?: boolean;
}

// Define the interface for the imperative handle
export interface MobileCameraCaptureHandle {
  clearPicture: () => void;
}

// Use forwardRef to allow parent components to get a ref to this component's instance
export const MobileCameraCapture = forwardRef<
  MobileCameraCaptureHandle,
  MobileCameraCaptureProps
>(({ onPictureTaken, showPreview }, ref) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Use a ref to keep track of the current object URL for cleanup purposes
  const objectUrlRef = useRef<string | null>(null);
  // Ref for the hidden file input element
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Effect for cleaning up the object URL
  useEffect(() => {
    // This cleanup function runs when the component unmounts
    // or before the effect runs again if dependencies change (none here).
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null; // Clear the ref after revoking
        console.log("Object URL revoked on unmount/cleanup.");
      }
    };
  }, []); // Empty dependency array ensures this runs only on mount and unmount

  // Function to revoke the existing URL if present
  const revokeExistingUrl = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
      console.log("Previous object URL revoked.");
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setError(null); // Clear previous errors

    // Revoke previous URL before processing the new file
    revokeExistingUrl();
    setPreviewUrl(null); // Clear previous preview immediately

    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];

      // Basic validation
      if (!file.type.startsWith("image/")) {
        setError("Please select an image file.");
        // Reset the file input value so the same file can be selected again if needed
        if (event.target) event.target.value = "";
        return;
      }

      // Create a new object URL for the file
      const newObjectUrl = URL.createObjectURL(file);
      objectUrlRef.current = newObjectUrl; // Store the new URL in the ref
      setPreviewUrl(newObjectUrl); // Update state to show preview
      console.log("New object URL created:", newObjectUrl);

      // Call the callback prop with timestamp and file
      onPictureTaken(Date.now(), file);
    } else {
      setError("No file selected.");
    }
    // Reset the file input value after processing (or error) to allow re-selection
    if (event.target) event.target.value = "";
  };

  // Trigger the input click handler programmatically via a button
  const triggerFileInput = () => {
    fileInputRef.current?.click(); // Use the ref to click the hidden input
  };

  // Expose the clearPicture function via useImperativeHandle
  useImperativeHandle(ref, () => ({
    clearPicture: () => {
      console.log("Clearing picture via imperative handle.");
      setError(null);
      setPreviewUrl(null);
      revokeExistingUrl(); // Revoke the URL when clearing
      // Also reset the hidden input's value
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
  }));

  return (
    <div>
      {/* Hidden file input with ref */}
      <input
        ref={fileInputRef} // Assign the ref
        type="file"
        accept="image/*"
        capture="environment" // Use "user" for front camera
        onChange={handleFileChange}
        style={{ display: "none" }} // Hide the default input UI
      />

      {/* Button to trigger the file input */}
      <Button text="Take Picture" type="primary" onClick={triggerFileInput}>
        <div className="flex gap-2 items-center">
          <CameraIcon
            size={34}
            color="#EF4444"
            className="hover:stroke-red-500"
          />
          <span>Take Picture</span>
        </div>
      </Button>

      {/* Error display */}
      {error && <p className="text-red-500 text-sm mb-2">{error}</p>}

      {/* Image Preview */}
      {previewUrl && showPreview && (
        <div
          className="mt-4 h-[50vh]"
          style={{
            backgroundImage: `url(${previewUrl})`,
            backgroundSize: "contain",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />
      )}
    </div>
  );
});

// Add a display name for better debugging
MobileCameraCapture.displayName = "MobileCameraCapture";
